import { useState, useRef, useEffect } from 'react'

import { Head, Layout, Title } from '@/components'
import { ConfirmReview, ReviewForm } from '@/components/Form'
import { useRouter } from 'next/router'
import { gql, useMutation, useQuery } from '@apollo/client'
import { CREATE_REVIEW_FOR_LISTING } from '@/queries'

interface IFormInputs {
  streetName: string
  streetNumber: string
  city: string
  neighbourhood: string
  region: string
  unitNumber: string
  country: string
  postalZip: string
  listingType: string
  parkingLocation: string
  laundryLocation: string
  buildingType: string
  rating: number
  bedrooms: number
  bathrooms: number
  content: string
  rentPrice: number
  rentFrequency: string
  shareTenancy: boolean
  stillTenant: boolean
  tenancyStart: Date
  tenancyEnd: Date
  petsWelcome: boolean
  petsWelcomeStory: string
  parkingAvgPrice: number
  noisyNeighbours: boolean
  noisyNeighboursStory: string
  landlordResponse: boolean
  landlordResponseStory: string
  wellMaintained: boolean
  wellMaintainedStory: string
  pests: boolean
  pestsStory: string
  landlordLanguages: boolean
  landlordLanguagesStory: string
  accessibility: boolean
  accessibilityStory: string
  dishwasherAvailable: boolean
  airConditioningIncluded: boolean
  covidFriendly: boolean
  laundryIncluded: boolean
  laundryAvgPrice: number
  dryerIncluded: boolean
  dryerAvgPrice: number
  gasIncluded: boolean
  gasAvgPrice: number
  hydroIncluded: boolean
  hydroAvgPrice: number
  waterIncluded: boolean
  waterAvgPrice: number
}

export default function NewListing() {
  const [formValues, setFormValues] = useState<IFormInputs | null>()
  const [step, setStep] = useState(0)
  const el = useRef<HTMLDivElement>()
  const { push, query } = useRouter()
  const {
    data: listingData,
    loading,
    error,
  } = useQuery(LISTING_BY_ID, {
    variables: {
      id: query.id,
    },
  })
  const [createNewReview] = useMutation(CREATE_REVIEW_FOR_LISTING)

  const handleUpdate = (data: any) => {
    setFormValues({
      ...formValues,
      ...data,
    })
    setStep(step + 1)
    if (el.current) {
      el.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    if (listingData) {
      const reviewData = { ...listingData.listingById }
      delete reviewData.reviews
      setFormValues(reviewData)
    }
  }, [listingData])

  const handleBack = () => {
    setStep(step > 0 ? step - 1 : 0)
    if (el.current) {
      el.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  const handleCreate = async (data) => {
    const review = await createNewReview({
      variables: {
        listingid: query.id,
        ...data,
      },
    })

    push(`/listing/${review.data.createNewReview.id}`)
  }

  const renderFormPart = (stepValue: number) => {
    switch (stepValue) {
      case 1:
        return <ConfirmReview handleCreate={handleCreate} handleBack={handleBack} data={formValues} />
      default:
        return <ReviewForm handleUpdate={handleUpdate} handleBack={handleBack} data={formValues} />
    }
  }

  return (
    <Layout className="lg:min-h-full relative ">
      <Head title="New Listing Review" description="Create new review" />
      <div className="text-left ">
        <header className="pt-10 max-w-screen-xl mx-auto px-6">
          <Title as="h1" size="2xl">
            Review your rental experience
          </Title>
        </header>
        <div className="relative lg:pb-24 lg:pt-6 ">
          <div className="max-w-screen-xl mx-auto px-6">
            <div className="scroll-my-24" ref={el}>
              {formValues && renderFormPart(Number(step))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

const LISTING_BY_ID = gql`
  query ListingById($id: String!) {
    listingById(id: $id) {
      id
      listingType
      unitNumber
      streetNumber
      streetName
      city
      country
      region
      neighbourhood
      postalzip
      forRent
      normalized_address
      longitude
      latitude
      updatedAt
      createdAt

      forRent
      reviews {
        images {
          id
          filePath
          fileType
          height
          width
          fileName
          fileId
          updatedAt
          createdAt
        }
      }
    }
  }
`
